import { useLayoutContext } from '@/utils/providers/layoutContext/useUserContext.tsx';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';

const THROTTLE_MS = 100;

const ScrollToBottom: FC = () => {
  const { fullScreenVideo } = useLayoutContext();

  const [isBottom, setIsBottom] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const rafRef = useRef<number | null>(null);
  const lastRunRef = useRef(0);

  const updateScrollState = useCallback(() => {
    if (fullScreenVideo) {
      setIsVisible(false);
      return;
    }

    const scrollBottom = Math.ceil(window.innerHeight + window.scrollY);
    const docHeight = document.documentElement.scrollHeight;

    setIsBottom(scrollBottom >= docHeight - 100);
    setIsVisible(scrollBottom < docHeight - 50);
  }, [fullScreenVideo]);

  useEffect(() => {
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastRunRef.current < THROTTLE_MS) {
        // Schedule one trailing update
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
          lastRunRef.current = Date.now();
          updateScrollState();
        });
        return;
      }
      lastRunRef.current = now;
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    updateScrollState();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [updateScrollState]);

  const handleScroll = () => {
    window.scrollTo({ behavior: 'smooth', top: isBottom ? 0 : document.body.scrollHeight });
  };

  return (
    <div>
      {isVisible && (
        <Button
          className="bottom-5 right-5 fixed z-10 p-1 btn-sm btn-icon btn-outline-secondary"
          onClick={handleScroll}
        >
          <svg
            className="overflow-visible w-6 h-6"
            height="100"
            viewBox="0 0 100 100"
            width="100"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isBottom ? (
              <path
                d="M775,597.22l-196.43-194.44-78.57-77.78-78.57,77.78-196.43,194.44,78.57,77.78,196.43-194.44,196.43,194.44,78.57-77.78Z"
                fill="currentColor"
                strokeWidth="0"
                transform="scale(.1)"
              />
            ) : (
              <path
                d="M225,402.78l196.43,194.44,78.57,77.78,78.57-77.78,196.43-194.44-78.57-77.78-196.43,194.44-196.43-194.44-78.57,77.78Z"
                fill="currentColor"
                strokeWidth="0"
                transform="scale(.1)"
              />
            )}
          </svg>
        </Button>
      )}
    </div>
  );
};

export default ScrollToBottom;
